import { useCallback, useEffect, useMemo, useState, ReactNode } from 'react';

import {
  applyThemeToDocument,
  getStoredTheme,
  getSystemTheme,
  resolveTheme,
  subscribeToSystemThemeChanges,
  ThemeContext,
  THEME_STORAGE_KEY,
  type ResolvedTheme,
  type Theme,
} from '@/app/contexts/theme';

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
}

export default function ThemeProvider({ children, defaultTheme }: ThemeProviderProps) {
  const [themeState, setThemeState] = useState<Theme>(() => defaultTheme ?? getStoredTheme());
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolveTheme(themeState));

  const applyTheme = useCallback((newResolvedTheme: ResolvedTheme) => {
    applyThemeToDocument(newResolvedTheme);
    setResolvedTheme(newResolvedTheme);
  }, []);

  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem(THEME_STORAGE_KEY, newTheme);
    applyTheme(resolveTheme(newTheme));
  }, [applyTheme]);

  const toggleTheme = useCallback(() => {
    setTheme(resolvedTheme === 'light' ? 'dark' : 'light');
  }, [resolvedTheme, setTheme]);

  useEffect(() => {
    applyTheme(resolveTheme(themeState));
  }, [themeState, applyTheme]);

  useEffect(function followTheSystemWhileNobodyHasChosen() {
    if (themeState !== 'system') return undefined;
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    return subscribeToSystemThemeChanges(mediaQuery, () => applyTheme(getSystemTheme()));
  }, [themeState, applyTheme]);

  const value = useMemo(() => ({
    theme: themeState,
    resolvedTheme,
    setTheme,
    toggleTheme,
  }), [themeState, resolvedTheme, setTheme, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
